import type {Evaluator} from "@cm/graph-v3"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {GeometryAttributeData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {visit} from "#template/template-node/visit-helpers"

// Wrapper-style operator: holds a unary or binary math operator node as its `operator` field. The
// concrete dim-variant pins which unary/binary types are accepted. Same pattern as
// `GeometryProductOperatorNode`: part of the geometry hierarchy, visiting the child operator and
// forwarding its evaluated data in `setupGeometryData`.
export type GeometryMathOperatorParameters<TOperator> = {
    name: string
    operator: TOperator
}

export abstract class GeometryMathOperatorNode<
    out T extends IndexingDim,
    out TOperator extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
    out P extends GeometryMathOperatorParameters<TOperator> = GeometryMathOperatorParameters<TOperator>,
> extends GeometryAttributeNode<T, P> {
    public override onFilterActive(context: OnFilterActiveContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<P> {
        visit(this.parameters.operator, evaluator)
        return super.onFilterActive(context, evaluator)
    }

    public override onCompile(context: OnCompileContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<P> {
        visit(this.parameters.operator, evaluator)
        return super.onCompile(context, evaluator)
    }

    protected override hasValidParameters(parameters: P): parameters is P & NonNullableParameters<P> {
        return parameters.operator !== null && parameters.operator !== undefined
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<P>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const evaluatedGeometryData = evaluator.evaluate(parameters.operator)
        return {evaluatedGeometryData}
    }
}
